import React, { ReactNode } from 'react';

import Icon from '../../Icon/Icon';
import { bemHelper, isNull } from '../../../helpers';
import getGlobalProps from '../../../helpers/constants/global-props';

export interface PinProps {
  /** Header contents */
  children?: ReactNode;
  /** Order status */
  order?: 'ASC' | 'DESC';
  /** Modifier for different text position in relation to the ordination arrows */
  right?: boolean;
  /** Remove the carat icon */
  noCarat?: boolean;
  /** Custom icon */
  customIcon?: ReactNode;
  /** Click callback */
  onClick?: () => void;
  /** @ignore */
  ariaLabel?: string;
}

const globalProps = getGlobalProps({ sunset: true, inkSass: true });

/**
 * Wrapper for header contents in sortable tables
 */
function Pin({ order, right, children, noCarat, customIcon, ariaLabel, onClick }: PinProps) {
  function definePinClassName(): string {
    const b = bemHelper();

    return b('ink-pin', {
      active: Boolean(order),
      hover: true,
    });
  }

  function definePinIconClassName(): string {
    const b = bemHelper();
    const direction = right ? 'right' : 'left';

    return b('ink-pin--icon', {
      [direction]: Boolean(children),
    });
  }

  function defineIconType(): string {
    if (noCarat) {
      return '';
    }
    if (isNull(order)) {
      return 'sort-carat';
    }
    if (order === 'ASC') {
      return 'up-carat';
    }
    return 'down-carat';
  }

  function getAriaSortValue(): 'descending' | 'ascending' | 'none' {
    if (order === 'DESC') return 'descending';
    if (order === 'ASC') return 'ascending';
    return 'none';
  }

  const icon = customIcon || <Icon aria-sort={getAriaSortValue()} type={defineIconType()} size="sm" />;
  const pinIcon = <span className={definePinIconClassName()}>{icon}</span>;

  return (
    /* We are disabling this rule here because the role="button" ensures the element can be pressed with enter */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <span
      onClick={onClick}
      role="button"
      className={definePinClassName()}
      tabIndex={0}
      aria-label={ariaLabel}
      {...globalProps}
    >
      {Boolean(right) && pinIcon}
      {children && <span className="ink-pin--text">{children}</span>}
      {Boolean(!right) && pinIcon}
    </span>
  );
}

Pin.defaultProps = {
  children: null,
  order: null,
  right: false,
  noCarat: false,
  customIcon: null,
  onClick: undefined,

  ariaLabel: null,
};

export default Pin;
